import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { graphqlApi } from '../../../services/graphql';
import { Spinner } from '../../common/Spinner';
import ChannelIcon from '../../campaigns/ChannelIcon';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { INFLUENCER_PAYOUT_RATE } from '../../../constants/platform';

const ITEM_LABEL: Record<string, string> = {
  reels: '릴스', story: '스토리', post: '포스트/피드', feed: '피드',
  blogPost: '포스팅', video: '영상', shorts: '쇼츠',
};

interface SubmissionRow {
  seq: number;
  channel: string;
  item: string;
  quantity: number;
  visitDate: string;
  deadline: string;
  submitted: boolean;
  contentLink?: string;
}

const ContentSubmitPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('campaigns');

  const [match, setMatch] = useState<any>(null);
  const [campaign, setCampaign] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitRowIdx, setSubmitRowIdx] = useState<number>(-1);
  const [contentLink, setContentLink] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    try {
      const matchRes = await graphqlApi.getMatch(matchId);
      if (matchRes.success && matchRes.data) {
        setMatch(matchRes.data);
        const campRes = await graphqlApi.getCampaign(matchRes.data.campaignId);
        if (campRes.success && campRes.data) {
          setCampaign(campRes.data);
          buildRows(campRes.data, matchRes.data);
        }
      }
    } catch (err) {
      console.error('Failed to load:', err);
      toast.error(t('contentSubmit.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [matchId, t]);

  const buildRows = (c: any, m: any) => {
    const pc = c.publishingConditions || {};
    const selectedMissions: string[] = m.selectedMissions || [];
    const visitStart = m.visitInfo?.startDate || c.visitStartDate || '-';
    const uploadDays = c.uploadDeadlineDays || 14;
    const existingSubs: any[] = m.contentSubmissions || [];
    const result: SubmissionRow[] = [];
    let seq = 1;
    for (const ch of selectedMissions) {
      const channelData = pc[ch];
      if (!channelData?.conditions) continue;
      for (const cond of channelData.conditions) {
        const deadlineDate = visitStart !== '-'
          ? new Date(new Date(visitStart).getTime() + uploadDays * 86400000).toISOString().slice(0, 10)
          : '-';
        // Check if already submitted
        const existing = existingSubs.find((s: any) => s.seq === seq);
        result.push({
          seq: seq++, channel: ch, item: cond.item, quantity: cond.quantity,
          visitDate: visitStart, deadline: deadlineDate,
          submitted: !!existing?.contentLink,
          contentLink: existing?.contentLink || undefined,
        });
      }
    }
    setRows(result);
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalCount = rows.length;
  const submittedCount = rows.filter(r => r.submitted).length;
  const pendingCount = totalCount - submittedCount;
  const rewardAmount = campaign?.rewardAmount ? Math.round(campaign.rewardAmount * INFLUENCER_PAYOUT_RATE) : 0;

  const openSubmitModal = (idx: number) => { setSubmitRowIdx(idx); setContentLink(rows[idx]?.contentLink || ''); setShowSubmitModal(true); };

  const handleSubmitContent = async () => {
    if (!contentLink.trim()) { toast.error(t('contentSubmit.linkRequired')); return; }
    if (!matchId) return;
    setSubmitting(true);
    try {
      const updatedRows = rows.map((r, i) => i === submitRowIdx ? { ...r, submitted: true, contentLink: contentLink.trim() } : r);
      // Persist to match DB
      const contentSubmissions = updatedRows
        .filter(r => r.submitted && r.contentLink)
        .map(r => ({ seq: r.seq, channel: r.channel, item: r.item, contentLink: r.contentLink, submittedAt: new Date().toISOString(), status: 'submitted' }));
      await graphqlApi.updateMatch(matchId, { contentSubmissions } as any);
      setRows(updatedRows);
      toast.success(t('contentSubmit.submitSuccess'));
      setShowSubmitModal(false);
    } catch { toast.error(t('contentSubmit.submitFailed')); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-64"><Spinner /></div>;
  if (!match || !campaign) return <div className="max-w-3xl mx-auto px-4 py-12 text-center text-gray-400">{t('contentSubmit.noData')}</div>;

  const c = campaign as any;
  const channels: string[] = match.selectedMissions || [];
  const channelLabel = (ch: string) => t(`applicants.channels.${ch}`, ch);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <button onClick={() => navigate('/my-profile/campaigns')} className="flex items-center text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeftIcon className="w-4 h-4 mr-1" /> {t('contentSubmit.backToMyCampaigns')}
      </button>

      <div className="flex gap-2">
        {channels.map(ch => (
          <span key={ch} className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
            <ChannelIcon channel={ch === 'blog' ? 'blog' : ch} className="w-3.5 h-3.5" />
            {channelLabel(ch)}
          </span>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 flex gap-4">
        <div className="w-32 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
          {c.representativeImages?.[0]?.fileUrl ? (
            <img src={c.representativeImages[0].fileUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">{t('contentSubmit.noImage')}</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900 mb-1">{c.campaignName}</h1>
          <p className="text-sm text-gray-500 line-clamp-2">{c.providedValueDescription || c.productName || ''}</p>
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <span>{t('contentSubmit.visitPeriod')} {c.visitStartDate?.slice(0, 10) || '-'} ~ {c.visitEndDate?.slice(0, 10) || '-'}</span>
            <span>{t('contentSubmit.uploadDeadline', { days: c.uploadDeadlineDays || 14 })}</span>
          </div>
        </div>
      </div>
      <hr className="border-gray-200" />

      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">{t('contentSubmit.missionTitle')}</h2>
        <div className="flex gap-3 flex-wrap">
          <div className="px-4 py-2 bg-gray-50 rounded-lg text-sm">
            <span className="text-gray-500">{t('contentSubmit.totalSubmissions')}</span> <span className="font-bold text-gray-900">{totalCount}{t('contentSubmit.items')}</span>
          </div>
          <div className="px-4 py-2 bg-green-50 rounded-lg text-sm">
            <span className="text-gray-500">{t('contentSubmit.submitted')}</span> <span className="font-bold text-green-600">{submittedCount}{t('contentSubmit.items')}</span>
          </div>
          <div className="px-4 py-2 bg-red-50 rounded-lg text-sm">
            <span className="text-gray-500">{t('contentSubmit.pending')}</span> <span className="font-bold text-red-600">{pendingCount}{t('contentSubmit.items')}</span>
          </div>
          <div className="px-4 py-2 bg-blue-50 rounded-lg text-sm">
            <span className="text-gray-500">{t('contentSubmit.contentReward')}</span> <span className="font-bold text-blue-600">${rewardAmount.toLocaleString()}</span>
            <span className="text-[10px] text-gray-400 ml-1">{t('contentSubmit.rewardDesc')}</span>
          </div>
        </div>
      </div>
      <hr className="border-gray-200" />

      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">{t('contentSubmit.submissionStatusTitle')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-2 px-3 font-medium">{t('contentSubmit.colSeq')}</th>
                <th className="py-2 px-3 font-medium">{t('contentSubmit.colChannel')}</th>
                <th className="py-2 px-3 font-medium">{t('contentSubmit.colItem')}</th>
                <th className="py-2 px-3 font-medium">{t('contentSubmit.colVisitDate')}</th>
                <th className="py-2 px-3 font-medium">{t('contentSubmit.colDeadline')}</th>
                <th className="py-2 px-3 font-medium">{t('contentSubmit.colSubmit')}</th>
                <th className="py-2 px-3 font-medium">{t('contentSubmit.colExtend')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-3 text-gray-700">{row.seq}</td>
                  <td className="py-3 px-3"><span className="flex items-center gap-1"><ChannelIcon channel={row.channel === 'blog' ? 'blog' : row.channel} className="w-4 h-4" /><span className="text-gray-700">{channelLabel(row.channel)}</span></span></td>
                  <td className="py-3 px-3 text-gray-700">{ITEM_LABEL[row.item] || row.item} {row.quantity}건</td>
                  <td className="py-3 px-3 text-gray-600">{row.visitDate}</td>
                  <td className="py-3 px-3 text-gray-600">{row.deadline}</td>
                  <td className="py-3 px-3">
                    {row.submitted ? (
                      <a href={row.contentLink} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline">{t('contentSubmit.submittedLabel')}</a>
                    ) : (
                      <button onClick={() => openSubmitModal(idx)} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">{t('contentSubmit.submitBtn')}</button>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <button onClick={() => navigate('/inquiry/new')} className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">{t('contentSubmit.extendBtn')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-xs text-yellow-800 space-y-1">
        <p>• {t('contentSubmit.cancelNotice1')}</p>
        <p>• {t('contentSubmit.cancelNotice2')}</p>
      </div>

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSubmitModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900">{t('contentSubmit.modalTitle')}</h3>
            <hr className="border-gray-200" />
            <div><span className="text-xs text-gray-400">{t('contentSubmit.modalCampaignName')}</span><p className="text-sm font-semibold text-gray-900">{c.campaignName}</p></div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contentSubmit.modalContentLink')}</label>
              <input type="url" value={contentLink} onChange={e => setContentLink(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowSubmitModal(false)} className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50">{t('contentSubmit.modalCancel')}</button>
              <button onClick={handleSubmitContent} disabled={submitting || !contentLink.trim()} className="flex-1 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50">{submitting ? t('contentSubmit.modalSubmitting') : t('contentSubmit.modalSubmit')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentSubmitPage;
